// src/features/auth/authSlice.js (Complete version)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Get user from localStorage
const user = JSON.parse(localStorage.getItem('user'));

const initialState = {
    user: user || null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: '',
    userRole: user?.role || 'user',
};

// Register user
export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData, thunkAPI) => {
        try {
            const response = await apiService.register(userData);

            // Store user data
            if (response.token) {
                localStorage.setItem('user', JSON.stringify(response));
            }

            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Registration failed'
            });
        }
    }
);

// Login user
export const loginUser = createAsyncThunk(
    'auth/login',
    async (userData, thunkAPI) => {
        try {
            const response = await apiService.login(userData);

            // Store user data
            if (response.token) {
                localStorage.setItem('user', JSON.stringify(response));
            }

            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Login failed'
            });
        }
    }
);

// Logout user
export const logoutUser = createAsyncThunk(
    'auth/logout',
    async (_, thunkAPI) => {
        try {
            await apiService.logout();
            localStorage.removeItem('user');
            return null;
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Logout failed'
            });
        }
    }
);

// Verify email
export const verifyEmail = createAsyncThunk(
    'auth/verifyEmail',
    async (tokenData, thunkAPI) => {
        try {
            return await apiService.verifyEmail(tokenData.token);
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Email verification failed'
            });
        }
    }
);

// Resend verification email
export const resendVerificationEmail = createAsyncThunk(
    'auth/resendVerification',
    async (_, thunkAPI) => {
        try {
            return await apiService.resendVerificationEmail();
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Failed to resend verification email'
            });
        }
    }
);

// Forgot password
export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async (emailData, thunkAPI) => {
        try {
            return await apiService.forgotPassword(emailData.email);
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Password reset request failed'
            });
        }
    }
);

// Verify reset token
export const verifyResetToken = createAsyncThunk(
    'auth/verifyResetToken',
    async (tokenData, thunkAPI) => {
        try {
            return await apiService.verifyResetToken(tokenData.token);
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Token verification failed'
            });
        }
    }
);

// Reset password
export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async (passwordData, thunkAPI) => {
        try {
            return await apiService.resetPassword(passwordData.token, passwordData.password);
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Password reset failed'
            });
        }
    }
);

// Update profile
export const updateProfile = createAsyncThunk(
    'auth/updateProfile',
    async (profileData, thunkAPI) => {
        try {
            const response = await apiService.updateProfile(profileData);

            // Update user in localStorage
            const currentUser = thunkAPI.getState().auth.user;
            const updatedUser = { ...currentUser, ...response };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Profile update failed'
            });
        }
    }
);

// Change password
export const changePassword = createAsyncThunk(
    'auth/changePassword',
    async (passwordData, thunkAPI) => {
        try {
            return await apiService.changePassword(passwordData);
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message || 'Password change failed'
            });
        }
    }
);

// Get current user
export const getCurrentUser = createAsyncThunk(
    'auth/getCurrentUser',
    async (_, thunkAPI) => {
        try {
            const response = await apiService.getCurrentUser();

            // Update user in localStorage
            if (response.token) {
                localStorage.setItem('user', JSON.stringify(response));
            }

            return response;
        } catch (error) {
            // If token is invalid, clear user data
            localStorage.removeItem('user');
            apiService.setToken(null);

            return thunkAPI.rejectWithValue({
                message: error.message || 'Failed to get user data'
            });
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        resetAuthState: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = '';
        },
        clearUser: (state) => {
            state.user = null;
            state.userRole = 'user';
            localStorage.removeItem('user');
            apiService.setToken(null);
        },
        setUser: (state, action) => {
            state.user = action.payload;
            state.userRole = action.payload?.role || 'user';
            if (action.payload) {
                localStorage.setItem('user', JSON.stringify(action.payload));
                if (action.payload.token) {
                    apiService.setToken(action.payload.token);
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Register cases
            .addCase(registerUser.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.user = action.payload;
                state.userRole = action.payload.role;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
                state.user = null;
            })

            // Login cases
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.user = action.payload;
                state.userRole = action.payload.role;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
                state.user = null;
            })

            // Logout case
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.userRole = 'user';
                state.isSuccess = false;
                state.isError = false;
                state.message = '';
            })

            // Verify email cases
            .addCase(verifyEmail.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(verifyEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                if (state.user) {
                    state.user.isVerified = true;
                    localStorage.setItem('user', JSON.stringify(state.user));
                }
            })
            .addCase(verifyEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Resend verification cases
            .addCase(resendVerificationEmail.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(resendVerificationEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = 'Verification email sent successfully';
            })
            .addCase(resendVerificationEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Forgot password cases
            .addCase(forgotPassword.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = 'Password reset email sent';
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Verify reset token cases
            .addCase(verifyResetToken.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(verifyResetToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
            })
            .addCase(verifyResetToken.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Reset password cases
            .addCase(resetPassword.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = 'Password reset successfully';
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Update profile cases
            .addCase(updateProfile.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateProfile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.user = { ...state.user, ...action.payload };
                state.message = 'Profile updated successfully';
            })
            .addCase(updateProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Change password cases
            .addCase(changePassword.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = 'Password changed successfully';
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Get current user cases
            .addCase(getCurrentUser.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.user = action.payload;
                state.userRole = action.payload.role;
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
                state.user = null;
                state.userRole = 'user';
            });
    },
});

export const { resetAuthState, clearUser, setUser } = authSlice.actions;
export default authSlice.reducer;

